import { publishDrillFooterContainerContentHeight } from "#use-drill-footer-container-content-height";
import { useIsFocused } from "@-/com.npmjs.expo-router/react-navigation";
import { AboveKeyboard } from "@-/dev.improve.haniverse.components.above-keyboard";
import { useLearnCardSurface } from "@-/dev.improve.haniverse.routes.nav.app.learn.deck.context";
import { cn } from "@-/com.npmjs.cn";
import type { ReactNode } from "react";
import { useCallback, useEffect, useRef } from "react";
import { View, type ViewProps } from "@-/com.npmjs.react-native";

/**
 * Keyboard-safe footer strip: wraps `children` in `AboveKeyboard` as a normal flex
 * child and applies width / z-index. While the route is unfocused — or, inside the Learn deck,
 * while this surface is not the learner's live one — renders no children, so a stacked route or a
 * background card never shows a stale footer or mounts an input that could claim focus.
 */
export function DrillFooterContainer({
  aboveKeyboardClassName,
  children,
  className,
  isKeyboardSticky = true,
}: {
  /** Forwarded to the inner `AboveKeyboard` wrapper (e.g. for `border-t`). */
  aboveKeyboardClassName?: string;
  children?: ReactNode;
  isKeyboardSticky?: boolean;
} & Pick<ViewProps, "className">) {
  const isFocused = useIsFocused();
  const { isActive: isSurfaceActive } = useLearnCardSurface();
  const isLive = isFocused && isSurfaceActive;

  const latestHeightRef = useRef(-1);

  useEffect(() => {
    return () => {
      publishDrillFooterContainerContentHeight(0);
    };
  }, []);

  const handleLayout = useCallback((e: { nativeEvent: { layout: { height: number } } }) => {
    const h = Math.round(e.nativeEvent.layout.height);
    if (h === latestHeightRef.current) {
      return;
    }
    latestHeightRef.current = h;
    publishDrillFooterContainerContentHeight(h);
  }, []);

  const content = (
    <>
      <View className={cn("z-10 w-full", className)} collapsable={false} pointerEvents="box-none">
        <View
          className="w-full"
          collapsable={false}
          onLayout={handleLayout}
          pointerEvents="box-none"
        >
          {isLive ? children : null}
        </View>
      </View>
    </>
  );

  if (!isKeyboardSticky) {
    return content;
  }

  return <AboveKeyboard className={aboveKeyboardClassName}>{content}</AboveKeyboard>;
}
